import React from 'react';
import { ArrowLeftRight, Globe } from 'lucide-react';
import { Language, LanguageDisplayNames } from '@/services/interfaces/Translation';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTranslation } from '@/contexts/I18nContext';
import { popularLanguages } from '@/i18n/types';

interface LanguagePairSelectorProps {
  disabled?: boolean;
  className?: string;
  compact?: boolean; // 简化模式，用于翻译页面
}

// 可复用的语言选择器组件
interface LanguageSelectorProps {
  value: Language;
  onChange: (value: string) => void;
  disabled: boolean;
  label: string;
  popularLanguages: Language[];
  allLanguages: Language[];
  getLanguageDisplay: (lang: Language) => { name: string; flag: string };
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  value,
  onChange,
  disabled,
  label,
  popularLanguages,
  allLanguages,
  getLanguageDisplay
}) => {
  const display = getLanguageDisplay(value);
  const { t } = useTranslation();

  return (
    <div className="flex-1 w-full space-y-3">
      <div className="text-center hidden md:block">
        <div className="text-4xl mb-2">{display.flag}</div>
        <label className="text-sm font-bold text-muted-foreground">
          {label}
        </label>
      </div>
      <div className="flex items-center gap-3 md:block">
        <div className="text-2xl md:hidden">{display.flag}</div>
        <Select 
          value={value} 
          onValueChange={onChange}
          disabled={disabled}
        >
          <SelectTrigger className="w-full h-14 text-base">
            <SelectValue>
              <div className="flex items-center gap-2">
                {/* <span className="text-xl hidden md:inline">{display.flag}</span> */}
                <span>{t(`language.${display.name}`)}</span>
              </div>
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectLabel>{t(`common.popular`)}</SelectLabel>
              {popularLanguages.map((lang) => {
                const { name, flag } = getLanguageDisplay(lang);
                return (
                  <SelectItem key={lang} value={lang}>
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{flag}</span>
                      <span>{t(`language.${name}`)}</span>
                    </div>
                  </SelectItem>
                );
              })}
            </SelectGroup>
            { allLanguages.length > 0  && (
              <SelectGroup>
                <SelectLabel>所有语言</SelectLabel>
                {allLanguages.filter(lang => !popularLanguages.includes(lang)).map((lang) => {
                  const { name, flag } = getLanguageDisplay(lang);
                  return (
                    <SelectItem key={lang} value={lang}>
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{flag}</span>
                        <span>{t(`language.${name}`)}</span>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectGroup>
            )};
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export const LanguagePairSelector: React.FC<LanguagePairSelectorProps> = ({ 
  disabled = false,
  className = "",
  compact = false
}) => {
  const { 
    sourceLanguage, 
    targetLanguage, 
    setSourceLanguage, 
    setTargetLanguage, 
    swapLanguages 
  } = useLanguage();

  const { t } = useTranslation();

  // 获取所有语言
  // const allLanguages = Object.values(Language);

  const handleSourceLanguageChange = (value: string) => {
    setSourceLanguage(value as Language);
  };

  const handleTargetLanguageChange = (value: string) => {
    setTargetLanguage(value as Language);
  };

  // 获取语言显示名称和旗帜
  const getLanguageDisplay = (lang: Language) => {
    const [name, flag] = LanguageDisplayNames[lang];
    return { name, flag };
  };

  const sourceDisplay = getLanguageDisplay(sourceLanguage);
  const targetDisplay = getLanguageDisplay(targetLanguage);

  // 简化模式 - 用于翻译页面
  if (compact) {
    return (
      <div className={`flex items-center justify-center ${className}`}>
        <div className="flex items-center justify-center gap-3 py-0">
          <div className="flex items-center gap-1.5 px-2 py-0 bg-muted/10 rounded-md">
            <span className="text-lg">{sourceDisplay.flag}</span>
            <span className="text-sm font-medium">{t(`language.${sourceLanguage}`)}</span>
          </div>
          <ArrowLeftRight className="w-4 h-4 text-muted-foreground" />
          <div className="flex items-center gap-1.5 px-2 py-0 bg-muted/10 rounded-md">
            <span className="text-lg">{targetDisplay.flag}</span>
            <span className="text-sm font-medium">{t(`language.${targetLanguage}`)}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* 主标题 */}
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold text-foreground flex items-center justify-center gap-2">
          <Globe className="w-6 h-6 text-primary" />
          {t('translation.selectLanguage')}
        </h2>
        <p className="text-muted-foreground">
          {t('translation.selectLanguageDescription')}
        </p>
      </div>

      {/* 语言选择卡片 */}
      <div className="bg-card border rounded-xl p-6 shadow-sm">
        <div className="flex flex-col md:flex-row items-center gap-4">
          {/* 源语言 */}
          <LanguageSelector
            value={sourceLanguage}
            onChange={handleSourceLanguageChange}
            disabled={disabled}
            label="From"
            popularLanguages={popularLanguages}
            allLanguages={[]}
            getLanguageDisplay={getLanguageDisplay}
          />

          {/* 交换按钮 */}
          <div className="flex flex-col items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={swapLanguages}
              disabled={disabled}
              className="h-12 w-12 rounded-full hover:rotate-180 hover:md:rotate-180 transition-all duration-500 border-2 hover:border-primary hover:bg-primary/10"
              title="Swap languages"
            >
              <ArrowLeftRight className="w-5 h-5 rotate-90 md:rotate-0" />
            </Button>
            <span className="text-xs text-muted-foreground">{t('ui.swap')}</span>
          </div>

          {/* 目标语言 */}
          <LanguageSelector
            value={targetLanguage}
            onChange={handleTargetLanguageChange}
            disabled={disabled}
            label="To"
            popularLanguages={popularLanguages}
            allLanguages={[]}
            getLanguageDisplay={getLanguageDisplay}
          />
        </div>
      </div>
    </div>
  );
}; 